import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV, WA_VALORACION, SOCIALS } from "@/lib/site";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-ink/85 backdrop-blur-xl border-b border-gold/20" data-testid="main-header">
        <div className="flex items-center justify-between px-5 sm:px-10 h-16 lg:h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img src="/images/logo-mark-gold.png" alt="Karina Méndez Studio" className="h-9 lg:h-11 w-auto" />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-serif text-lg tracking-wide text-cream">Karina Méndez</span>
              <span className="font-mono text-[9px] tracking-[0.4em] text-gold uppercase mt-1">Studio</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-6" data-testid="nav-desktop">
            {NAV.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                data-testid={`nav-link-${n.id}`}
                className={({ isActive }) =>
                  `font-mono text-[11px] uppercase tracking-[0.18em] gold-underline transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-cream/70 hover:text-cream"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={WA_VALORACION}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-whatsapp-cta"
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-gold/50 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <MessageCircle size={14} />
              Reservar
            </a>
            <button
              onClick={() => setOpen(!open)}
              data-testid="nav-menu-toggle"
              aria-label="Abrir menú"
              className="xl:hidden text-cream p-2"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-ink flex flex-col justify-center px-8"
            data-testid="nav-mobile-menu"
          >
            <nav className="space-y-2">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.id}
                  initial={{ opacity: 0, y: 26 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={n.to}
                    data-testid={`nav-mobile-link-${n.id}`}
                    className={({ isActive }) =>
                      `block font-serif text-4xl py-1 ${isActive ? "text-gold italic" : "text-cream"}`
                    }
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-12 flex gap-6 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
            >
              {SOCIALS.map((s) => (
                <a key={s.name} href={s.url} target="_blank" rel="noreferrer" data-testid={`nav-social-${s.name.toLowerCase()}`}>
                  {s.name}
                </a>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
